import { take } from 'rxjs';
/**
 * libs
 */
import { type MqttClient } from 'mqtt';

/**
 * interfaces
 */
import { ITopicOptions, TopicFilter } from '../../interfaces';

/**
 * utilities
 */
import { MqttUtilities } from '../../utilities';

/**
 * models
 */
import { TopicHandler } from "./topic-handler";


/**
 * Central registry for managing TopicHandler instances.
 * @class TopicRegistry
 * @description
 * - Implements the Registry/Flyweight pattern for topics.
 * - Ensures that only one `TopicHandler` exists for any given topic filter string.
 * - Manages the lifecycle of all active topic subscriptions for a connection.
 */
export class TopicRegistry {

    /**
     * Internal map of active topics.
     * Key: the filter's normalized string form (see {@link _key}), Value: TopicHandler instance.
     * @private
     * @description
     * Keyed by string rather than by the `TopicFilter` itself because array and
     * subscription-map filters are objects: `MqttUtilities.buildTopicFilter`
     * returns a fresh array every call, so an identity-keyed map would miss on
     * every lookup and create a duplicate handler for each subscribe.
     */
    private _topics: Map<string, TopicHandler> = new Map<string, TopicHandler>();

    /**
     * Returns an array of all active TopicHandler instances.
     * @public
     */
    public get topicsArray(): TopicHandler[] {
        return Array.from(this._topics.values());
    }

    /**
     * Creates an instance of TopicRegistry.
     * @constructor
     * @param {MqttClient} _client - The MQTT client used for subscriptions.
     * @throws {Error} If client is invalid.
     */
    constructor(private readonly _client: MqttClient) {

        // global validation
        // Simple duck-typing check for MqttClient to avoid strict prototype issues
        if (this._client && !Object.hasOwn(this._client, 'connected')) {
            throw new Error('Client must be an instance of MqttClient');
        }
        
    }
    
    /**
     * Retrieves an existing TopicHandler if present.
     * @public
     * @template TPayload - Expected payload type.
     * @param {TopicFilter} topic - The topic to look up.
     * @returns {TopicHandler<TPayload> | undefined} The handler or undefined.
     */
    public topic<TPayload = any>(topic: TopicFilter): TopicHandler<TPayload> | undefined {
        return this._topics.get(this._key(topic));
    }

    /**
     * Returns every handler whose subscription filter covers an incoming topic.
     * @public
     * @template TPayload - Expected payload type.
     * @param {string} topic - The concrete topic a message arrived on.
     * @returns {TopicHandler<TPayload>[]} The matching handlers, empty when none.
     * @description
     * Handlers are registered under the filter they subscribed with, but
     * messages are delivered on a concrete topic. Those are the same string
     * only for a plain single-topic subscription — an array subscription or a
     * wildcard needs matching, or its messages are silently dropped.
     *
     * More than one handler can match the same message, which is intended: a
     * watcher of one meter and a watcher of every meter both want the frame.
     */
    public matchingTopics<TPayload = any>(topic: string): TopicHandler<TPayload>[] {

        // Fast path — a plain subscription is by far the common case, and it
        // keeps the exact-match lookup at its original cost.
        const exact = this._topics.get(topic);

        if (exact) {
            return [exact as TopicHandler<TPayload>];
        }

        return this.topicsArray.filter((handler) =>
            this._filterStrings(handler.topic).some((filter) => MqttUtilities.matchesTopicFilter(topic, filter)),
        );

    }

    /**
     * Flattens a topic filter to the plain filter strings it subscribes to.
     * @private
     * @param {TopicFilter} topic - A filter in any of its three shapes.
     * @returns {string[]} The individual filter strings.
     */
    private _filterStrings(topic: TopicFilter): string[] {

        if (typeof topic === 'string') {
            return [topic];
        }

        if (Array.isArray(topic)) {
            return topic.filter((entry): entry is string => typeof entry === 'string');
        }

        // Subscription map: the topics are its keys, the values are options.
        if (topic != null && typeof topic === 'object') {
            return Object.keys(topic);
        }

        return [];

    }

    /**
     * Manually registers a TopicHandler.
     * @public
     * @param {TopicFilter} topic - The topic key.
     * @param {TopicHandler} handler - The handler instance.
     */
    public add(topic: TopicFilter, handler: TopicHandler): void {
        this._exceptionOnInvalidTopicFilter(topic);
        this._topics.set(this._key(topic), handler);
    }

    /**
     * Unregisters a topic.
     * Note: This does not automatically destroy the handler; call `handler.destroy()` first if needed.
     * @public
     * @param {TopicFilter} topic - The topic to remove.
     */
    public remove(topic: TopicFilter): void {
        this._topics.delete(this._key(topic));
    }

    /**
     * Retrieves an existing handler or creates a new one if it doesn't exist.
     * This is the primary method for getting topic subscriptions.
     * @public
     * @template TPayload - Expected payload type.
     * @param {TopicFilter} topic - The topic to subscribe to.
     * @param {Partial<ITopicOptions<TPayload>>} [options] - The options for the topic.
     * @returns {TopicHandler<TPayload>} The active topic handler.
     */
    public createOrGetTopic<TPayload = any>(topic: TopicFilter, options?: Partial<ITopicOptions<TPayload>>): TopicHandler<TPayload> {

        // global validation
        this._exceptionOnInvalidTopicFilter(topic);
        
        // If the topic already exists, return the existing topic handler
        const existing = this._topics.get(this._key(topic));

        if (existing) {
            return existing;
        }
        
        // If the topic does not exist, create a new topic handler
        const topicHandler = new TopicHandler<TPayload>(this._client, topic, options);
        this.add(topic, topicHandler);

        // Automatically remove the topic handler when it shuts down
        this._autoRemoveTopicHandler(topicHandler);

        return topicHandler;

    }

    /**
     * Checks if a topic is currently registered.
     * @public
     * @param {TopicFilter} topic - The topic to check.
     * @returns {boolean} True if registered.
     */
    public has(topic: TopicFilter): boolean {
        return this._topics.has(this._key(topic));
    }

    /**
     * Normalizes a topic filter to the string this registry keys it by.
     * @private
     * @param {TopicFilter} topic - A filter in any of its three shapes.
     * @returns {string} A stable key, identical for two equal filters that are
     * different objects.
     */
    private _key(topic: TopicFilter): string {
        return this._filterStrings(topic).join('\n');
    }

    /**
     * Automatically removes a topic handler when it shuts down.
     * @private
     * @param {TopicHandler} topicHandler - The topic handler to remove.
     */
    private _autoRemoveTopicHandler(topicHandler: TopicHandler): void {
        topicHandler
            .shutdown$
            .pipe(take(1))
            .subscribe(() => {
                this.remove(topicHandler.topic);
            });
    }

    /**
     * Returns a string representation of the registry.
     * @public
     */
    public toString(): string {
        return `TopicRegistry(topics: ${this._topics.size})`;
    }

    /**
     * Destroys all registered topic handlers and clears the registry.
     * @public
     */
    public destroy(): void {
        this._topics.forEach(topicHandler => topicHandler.destroy());
        this._topics.clear();
    }

    /**
     * Validates the topic filter format.
     * @private
     * @throws {Error} If topic is invalid.
     */
    private _exceptionOnInvalidTopicFilter(topic: TopicFilter): void {


        // global validation
        if (topic == null) {
            throw new Error('Topic must be a non-empty string, string array, or subscription map');
        }
        else if (typeof topic === 'string' && topic.length === 0) {
            throw new Error('Topic must be a non-empty string');
        }
        else if (Array.isArray(topic) && topic.length === 0) {
            throw new Error('Topic must be a non-empty array');
        }
        else if (typeof topic === 'object' && Object.keys(topic).length === 0) {
            throw new Error('Topic must be a non-empty subscription map');
        }

    }

    
}
